<script>
	import { onMount } from "svelte";
	import { fade } from "svelte/transition";
	import { meditationMusicsAll as all, categoryAll } from "../../stores";
	import { Dates } from "../../utils/date";
	import { getResourceUrl } from "../../utils/resource";
	import TableToolbarNoPaging from "../../components/TableToolbarNoPaging.svelte";
	import { menu, menuSub, menuSubSub } from "../../stores";
	import { ErrorCodes } from "../../define/define";
	import { postFileApi, putFileApi, delApi, putApi } from "../../service/api";
	import { getCategoryBorderColor } from "../../utils/musicSleep";
	import {
		getHRLevelText,
		getHRLevelBgColor,
		getHRLevelBorderColor,
		getHRLevelBadgeColor,
		generateMusicId,
		getImageUrl,
		handleImageError,
		ACTIVE_OPTIONS,
		HR_LEVEL_OPTIONS,
		SORT_OPTIONS,
	} from "../../utils/musicCommon";
	import { validateFile, handleImagePreview } from "../../utils/file";
	import { playAudio, stopAudio } from "../../utils/audio";
	import { downloadFile } from "../../utils/download";

	// 상수 정의
	const BASE_URL = "/contents/music/meditations";

	// 상태 관리
	let isEditMode = false;
	let editingRowId = null;
	let filteredData = [];
	let currentSort = "updated";
	let currentSortDirection = "asc";
	let searchKeyword = "";

	// 편집 아이템 초기값
	const defaultEditItem = {
		id: 0,
		idCategory: null,
		idMusic: "",
		subjectKo: "",
		subjectEn: "",
		subjectJa: "",
		subjectZhCn: "",
		subjectZhTw: "",
		path: "",
		image: "",
		group: 1,
		isActive: false,
		createdAt: null,
		updatedAt: null,
	};

	// 상세보기 아이템 초기값
	const defaultDetailItem = {
		id: 0,
		idCategory: null,
		idMusic: "",
		subjectKo: "",
		subjectEn: "",
		subjectJa: "",
		subjectZhCn: "",
		subjectZhTw: "",
		path: "",
		image: "",
		group: 1,
		isActive: false,
		createdAt: null,
		updatedAt: null,
	};

	let editItem = { ...defaultEditItem };

	// 모달 상태 관리
	let showModal = false;
	let modalTitle = "";

	// 상세보기 모달 상태 관리
	let showDetailModal = false;
	let detailItem = { ...defaultDetailItem };

	// 카테고리 데이터
	let categories = [];

	// 카테고리 정보 가져오기
	const loadCategories = async () => {
		await categoryAll.fetch("/contents/category");
		// 명상용 카테고리(type: 2) 필터링, 없으면 수면용(type: 1) 사용
		let meditationCategories = ($categoryAll.data || []).filter((cat) => cat.type === 2);
		if (meditationCategories.length === 0) {
			meditationCategories = ($categoryAll.data || []).filter((cat) => cat.type === 1);
		}
		categories = meditationCategories;

		// 카테고리가 있으면 첫 번째 카테고리의 ID를 기본값으로 설정
		if (categories.length > 0) {
			defaultEditItem.idCategory = categories[0].id;
			defaultDetailItem.idCategory = categories[0].id;
		}
	};

	// 모달 상태 관리
	let activeCategory = null;

	// 이미지 미리보기 URL 상태
	let imagePreviewUrl = "";

	// 상태 추가
	let audioPlayer = null;
	let isPlaying = false;

	// 상태 추가 - 선택된 파일 관리
	let selectedFiles = {
		image: null,
		audio: null,
	};

	// 상태 추가: 저장 중 여부
	let isSaving = false;

	// 현재 재생 중인 아이템 ID 추적용 상태 추가
	let currentPlayingId = null;

	// 입력 핸들러 최적화
	const handleInput = (field, value) => {
		// 값이 실제로 변경된 경우에만 업데이트
		if (editItem[field] !== value) {
			editItem[field] = value;

			// 신규 등록 시 카테고리나 HR Level이 변경되면 음원 고유번호 자동 생성
			if (!editItem.id && (field === "idCategory" || field === "group")) {
				const categoryId = field === "idCategory" ? value : editItem.idCategory;
				const hrLevel = field === "group" ? value : editItem.group;

				// 해당 카테고리의 마지막 음원 번호 찾기
				const categoryItems = $all.data?.list?.filter((item) => item.idCategory === categoryId) || [];
				const lastNumber = categoryItems.length > 0 ? Math.max(...categoryItems.map((item) => parseInt(item.idMusic) || 0)) % 1000 : 0;

				editItem.idMusic = generateMusicId(categoryId, hrLevel, lastNumber);
			}
		}
	};

	// 메서드
	const setFocus = (field = "subjectKo") => {
		const element = document.getElementById(field);
		if (element) {
			element.focus();
		}
	};

	const validateItem = () => {
		if (!editItem.subjectKo) {
			alert("한글 음원명을 입력하세요.");
			setFocus("subjectKo");
			return false;
		}
		if (!editItem.subjectEn) {
			alert("영문 음원명을 입력하세요.");
			setFocus("subjectEn");
			return false;
		}
		if (!editItem.subjectJa) {
			alert("일본어 음원명을 입력하세요.");
			setFocus("subjectJa");
			return false;
		}
		if (!editItem.subjectZhCn) {
			alert("중국어 간체 음원명을 입력하세요.");
			setFocus("subjectZhCn");
			return false;
		}
		if (!editItem.subjectZhTw) {
			alert("중국어 번체 음원명을 입력하세요.");
			setFocus("subjectZhTw");
			return false;
		}
		if (!editItem.idMusic) {
			alert("음원 고유번호를 입력하세요.");
			setFocus("idMusic");
			return false;
		}
		if (!editItem.image) {
			alert("음원 이미지를 등록 하세요.");
			setFocus("image");
			return false;
		}
		if (!editItem.path) {
			alert("음원을 등록 하세요.");
			setFocus("path");
			return false;
		}
		return true;
	};

	// 저장 핸들러
	const handleSave = async () => {
		isSaving = true;
		try {
			console.log("명상 음원 저장 시작, 현재 editItem:", editItem);
			console.log("선택된 파일:", selectedFiles);

			if (!validateItem()) {
				console.log("유효성 검사 실패");
				return;
			}

			let result;
			let formData = new FormData();
			formData.append("idCategory", editItem.idCategory);
			formData.append("idMusic", editItem.idMusic);
			formData.append("subjectKo", editItem.subjectKo);
			formData.append("subjectEn", editItem.subjectEn);
			formData.append("subjectJa", editItem.subjectJa);
			formData.append("subjectZhCn", editItem.subjectZhCn);
			formData.append("subjectZhTw", editItem.subjectZhTw);
			formData.append("path", selectedFiles.audio);
			formData.append("image", selectedFiles.image);
			formData.append("group", editItem.group);

			if (editItem.id) {
				console.log("수정 모드 - PUT 요청");
				console.log("PUT 요청 URL: /contents/music/meditation");
				formData.append("isActive", editItem.isActive);
				result = await putFileApi(`/contents/music/meditation`, formData);
			} else {
				console.log("신규 등록 모드 - POST 요청");
				console.log("POST 요청 URL: /contents/music/meditation");
				result = await postFileApi("/contents/music/meditation", formData);
			}

			console.log("API 응답 결과:", result);

			switch (result.resultCode) {
				case ErrorCodes.OK:
					if (editItem.id) {
						alert("음원 수정이 완료되었습니다.");
					} else {
						alert("음원 등록이 완료되었습니다.");
					}
					editItem = { ...defaultEditItem };
					selectedFiles = { image: null, audio: null };
					imagePreviewUrl = "";
					closeModal();
					await fnSearch();
					break;
				default:
					alert(result.resultDescKo);
					break;
			}
		} finally {
			isSaving = false;
		}
	};

	const fnSearch = async () => {
		const url = `${BASE_URL}/1000/1/${searchKeyword ? encodeURIComponent(searchKeyword) : "ALL"}`;
		await all.fetch(url);
		resetEdit();
		if ($all?.data) {
			filteredData = getFilteredData($all.data);
		}
	};

	const fnReload = () => {
		searchKeyword = "";
		currentSort = "updated";
		currentSortDirection = "asc";
		fnSearch();
	};

	const resetEdit = () => {
		isEditMode = false;
		editingRowId = null;
		editItem = { ...defaultEditItem };
		if (categories.length > 0) {
			editItem.idCategory = categories[0].id;
		}
	};

	const fnProcessRegist = () => {
		const categoryId = categories.length > 0 ? categories[0].id : 0;
		// 해당 카테고리의 마지막 음원 번호 찾기
		const categoryItems = $all.data?.list?.filter((item) => item.idCategory === categoryId) || [];
		const lastNumber = categoryItems.length > 0 ? Math.max(...categoryItems.map((item) => parseInt(item.idMusic) || 0)) % 1000 : 0;

		editItem = {
			...defaultEditItem,
			idCategory: categoryId,
			idMusic: generateMusicId(categoryId, 1, lastNumber), // 기본 HR Level 1(HI)로 생성
		};
		modalTitle = '<i class="bx bx-plus-circle me-1"></i>음원 신규 등록';
		// 파일 관련 상태 초기화
		selectedFiles = { image: null, audio: null };
		imagePreviewUrl = "";
		showModal = true;
		setTimeout(() => setFocus("subjectKo"), 100);
	};

	const fnProcessEdit = (item) => {
		editItem = { ...item };
		modalTitle = '<i class="bx bx-edit me-1"></i>음원 정보 편집';
		// 파일 관련 상태 초기화
		selectedFiles = { image: null, audio: null };
		imagePreviewUrl = "";
		if (showDetailModal) {
			closeDetailModal();
		}
		showModal = true;
		setTimeout(() => setFocus("subjectKo"), 100);
	};

	const fnDelete = async (id) => {
		const item = $all.data.list.find((item) => item.id === id);
		if (item.isActive) {
			alert("노출 중인 음원은 삭제할 수 없습니다.");
			return;
		}

		if (confirm("정말 삭제하시겠습니까?")) {
			await delApi("/contents/music/meditation", { id });
			await fnSearch();
		}
	};

	const fnRegistServer = async () => {
		if (confirm("서버에 적용하시겠습니까?\n홈화면 캐시화와, 음원 목록 캐시화 작업이 진행됩니다.")) {
			const result = await putApi("/contents/music/meditation/cache", {});
			if (result.resultCode === ErrorCodes.OK) {
				alert("서버 적용이 완료되었습니다.");
			} else {
				alert(result.resultDescKo);
			}
		}
	};

	// 필터링된 데이터를 반환하는 함수
	const getFilteredData = (data) => {
		if (!data?.list) return [];

		let list = data.list;

		if (activeCategory || searchKeyword) {
			list = [...list];

			if (activeCategory) {
				list = list.filter((item) => item.idCategory === activeCategory);
			}

			if (searchKeyword) {
				const keyword = searchKeyword.toLowerCase();
				list = list.filter((item) => item.subjectKo.toLowerCase().includes(keyword) || item.subjectEn.toLowerCase().includes(keyword));
			}
		}

		return sortData(list);
	};

	// 정렬 함수
	const sortData = (list) => {
		const sortedList = [...list];
		const direction = currentSortDirection === "asc" ? 1 : -1;

		switch (currentSort) {
			case "updated":
				sortedList.sort((a, b) => direction * (new Date(b.updatedAt) - new Date(a.updatedAt)));
				break;
			case "created":
				sortedList.sort((a, b) => direction * (new Date(b.createdAt) - new Date(a.createdAt)));
				break;
			case "title":
				sortedList.sort((a, b) => direction * a.subjectKo.localeCompare(b.subjectKo));
				break;
			case "category":
				sortedList.sort((a, b) => {
					const catA = getCategoryName(a.idCategory);
					const catB = getCategoryName(b.idCategory);
					return direction * catA.localeCompare(catB);
				});
				break;
			case "active":
				sortedList.sort((a, b) => {
					const activeA = a.isActive ? 1 : 0;
					const activeB = b.isActive ? 1 : 0;
					return direction * (activeB - activeA);
				});
				break;
			case "level":
				sortedList.sort((a, b) => direction * (b.group - a.group));
				break;
		}

		return sortedList;
	};

	// 반응성 선언
	$: if ($all?.data) {
		filteredData = getFilteredData($all.data);
	}

	$: if (currentSort || currentSortDirection) {
		if ($all?.data) {
			filteredData = getFilteredData($all.data);
		}
	}

	$: if (activeCategory !== undefined) {
		if ($all?.data) {
			filteredData = getFilteredData($all.data);
		}
	}

	// 모달 닫기 핸들러
	const closeModal = () => {
		showModal = false;
		resetEdit();
		imagePreviewUrl = "";
		selectedFiles.image = null;
		selectedFiles.audio = null;
		stopAudio(
			audioPlayer,
			(v) => (audioPlayer = v),
			(v) => (isPlaying = v)
		);
		currentPlayingId = null; // 현재 재생 중인 아이템 ID 초기화
		// 파일 input들 초기화
		const imageFile = document.getElementById("imageFile");
		const audioFile = document.getElementById("audioFile");
		if (imageFile) imageFile.value = "";
		if (audioFile) audioFile.value = "";
	};

	// 이미지 선택 취소 핸들러
	const handleCancelImage = () => {
		imagePreviewUrl = "";
		editItem.image = "";
		selectedFiles.image = null;
	};

	const showDetail = (item) => {
		detailItem = { ...item };
		showDetailModal = true;
	};

	const closeDetailModal = () => {
		showDetailModal = false;
		detailItem = { ...defaultDetailItem };
		stopAudio(
			audioPlayer,
			(v) => (audioPlayer = v),
			(v) => (isPlaying = v)
		);
		currentPlayingId = null; // 현재 재생 중인 아이템 ID 초기화
	};

	// 카테고리 이름 가져오기
	const getCategoryName = (idCategory) => {
		const category = categories.find((cat) => cat.id === idCategory);
		return category ? category.subjectKo : "미분류";
	};

	const setActiveCategory = (categoryId) => {
		activeCategory = categoryId;
	};

	// 목록에서 음원 재생/정지 핸들러 추가
	const handleListAudioPlay = (item) => {
		if (currentPlayingId === item.id && isPlaying) {
			// 현재 재생 중인 아이템이면 정지
			stopAudio(
				audioPlayer,
				(v) => (audioPlayer = v),
				(v) => (isPlaying = v)
			);
			currentPlayingId = null;
		} else {
			// 다른 아이템이면 재생
			if (isPlaying) {
				// 이미 다른 음원이 재생 중이면 먼저 정지
				stopAudio(
					audioPlayer,
					(v) => (audioPlayer = v),
					(v) => (isPlaying = v)
				);
			}

			playAudio(
				item.path,
				{}, // 목록에서는 업로드된 파일이 아닌 기존 파일을 재생
				(v) => (audioPlayer = v),
				(v) => (isPlaying = v),
				getResourceUrl
			);
			currentPlayingId = item.id;
		}
	};

	onMount(() => {
		menu.set("콘텐츠 관리");
		menuSub.set("음원 관리");
		menuSubSub.set("명상 음원");
		loadCategories();
		fnSearch();
	});
</script>

<div class="card mb-4">
	<div class="p-3">
		<!-- 카테고리 탭 -->
		<ul class="nav nav-tabs d-flex" role="tablist">
			<li class="nav-item">
				<button
					class="nav-link {!activeCategory ? 'active border-top border-start border-end border-secondary' : ''}"
					style="cursor: pointer;"
					on:click={() => setActiveCategory(null)}
				>
					전체 명상음원
				</button>
			</li>
			{#each categories as category}
				<li class="nav-item">
					<button
						class="nav-link {activeCategory === category.id ? 'active border-top border-start border-end border-secondary' : ''}"
						style="cursor: pointer;"
						on:click={() => setActiveCategory(category.id)}
					>
						{category.subjectKo}
					</button>
				</li>
			{/each}
			<div class="ms-auto d-flex align-items-center flex-grow-1" style="padding: 5px;">
				<div class="d-flex align-items-center flex-grow-1 me-2 justify-content-end">
					<input
						type="text"
						class="form-control form-control-sm me-2"
						placeholder="검색어를 입력하세요"
						style="width: 200px;"
						value={searchKeyword}
						on:input={(e) => (searchKeyword = e.target.value)}
						on:keydown={(e) => {
							if (e.key === "Enter") {
								fnSearch();
							}
						}}
					/>
					<select class="form-select form-select-sm" style="width: auto;" on:change={(e) => (currentSort = e.target.value)}>
						{#each SORT_OPTIONS as option}
							{#if !activeCategory || option.value !== "category"}
								<option value={option.value} selected={currentSort === option.value}>
									{option.text}
								</option>
							{/if}
						{/each}
					</select>
				</div>
				<div class="btn-group me-2">
					<button
						type="button"
						class="btn btn-sm {currentSortDirection === 'asc' ? 'btn-primary' : 'btn-outline-primary'}"
						title="오름차순"
						on:click={() => (currentSortDirection = "asc")}
					>
						<i class="bx bx-sort-up"></i>
					</button>
					<button
						type="button"
						class="btn btn-sm {currentSortDirection === 'desc' ? 'btn-primary' : 'btn-outline-primary'}"
						title="내림차순"
						on:click={() => (currentSortDirection = "desc")}
					>
						<i class="bx bx-sort-down"></i>
					</button>
				</div>
				<TableToolbarNoPaging onReload={fnReload} onRegist={fnProcessRegist} onRegistServer={fnRegistServer} />
			</div>
		</ul>

		<!-- 신규 등록 폼 -->
		{#if filteredData.length === 0}
			<div class="text-center p-5">
				<i class="bx bx-info-circle fs-1 text-muted"></i>
				<p class="text-muted mt-3">데이터가 없습니다.</p>
			</div>
		{:else}
			<div class="row g-3" style="display: flex; flex-wrap: wrap;">
				{#each filteredData as item, index (`${item.id}_${index}`)}
					{#if isEditMode && editingRowId === item.id}
						<!-- 편집 폼 -->
						<div style="width: 270px; margin-bottom: 1rem; padding: 0 0.5rem;">
							<div class="card h-100 border {getCategoryBorderColor(editItem.idCategory)} border-opacity-50">
								<div class="card-header bg-primary bg-opacity-10">
									<h5 class="card-title mb-0">음원 편집</h5>
								</div>
								<div class="position-relative">
									{#if editItem.image}
										<div style="height: 150px; overflow: hidden;">
											<img
												src={editItem.image}
												alt={editItem.subjectKo}
												class="w-100 h-100 card-img-top"
												style="object-fit: cover; object-position: center;"
											/>
										</div>
									{:else}
										<div style="height: 150px; overflow: hidden;">
											<img src="/assets/img/nope-not-here.avif" alt="이미지 없음" style="width: 281px; height: 262px;" />
										</div>
									{/if}
								</div>
								<div class="card-body">
									<div class="mb-2">
										<label for="editCategoryIndex" class="form-label">분류 인덱스</label>
										<select
											id="editCategoryIndex"
											class="form-control form-control-sm"
											value={editItem.idCategory}
											on:change={(e) => handleInput("idCategory", parseInt(e.target.value))}
										>
											{#each categories as category}
												<option value={category.id}>
													{category.subjectKo} ({category.id})
												</option>
											{/each}
										</select>
									</div>
									<div class="mb-2">
										<label for="group" class="form-label">HR Level</label>
										<select
											class="form-select form-select-sm"
											id="group"
											value={editItem.group}
											on:change={(e) => handleInput("group", parseInt(e.target.value))}
										>
											{#each HR_LEVEL_OPTIONS as option}
												<option value={option.value}>{option.text}</option>
											{/each}
										</select>
									</div>
									<div class="mb-2">
										<label for="idMusic" class="form-label">음원 고유번호</label>
										<input
											type="number"
											class="form-control form-control-sm"
											value={editItem.idMusic}
											on:input={(e) => handleInput("idMusic", e.target.value)}
											on:focus={() => setFocus("idMusic")}
										/>
									</div>
									<div class="mb-2">
										<label for="subjectKo" class="form-label">한글명</label>
										<input
											type="text"
											class="form-control form-control-sm"
											value={editItem.subjectKo}
											on:input={(e) => handleInput("subjectKo", e.target.value)}
											on:focus={() => setFocus("subjectKo")}
											placeholder="한글 음원명을 입력하세요"
										/>
									</div>
									<div class="mb-2">
										<label for="subjectEn" class="form-label">영문명</label>
										<input
											type="text"
											class="form-control form-control-sm"
											value={editItem.subjectEn}
											on:input={(e) => handleInput("subjectEn", e.target.value)}
											on:focus={() => setFocus("subjectEn")}
											placeholder="영문 음원명을 입력하세요"
										/>
									</div>
									<div class="mb-2">
										<label for="image" class="form-label">이미지 경로</label>
										<div class="input-group input-group-sm">
											<input
												type="text"
												class="form-control form-control-sm"
												id="image"
												value={editItem.image}
												readOnly
												on:click={() => document.getElementById("imageFile").click()}
												placeholder="예: /v3/resources/sleep/17_Jazz/3005-47.jpg"
											/>
											<label class="input-group-text" for="imageFile">
												<i class="bx bx-upload"></i>
											</label>
											<input
												type="file"
												id="imageFile"
												accept="image/*"
												class="d-none"
												on:change={(e) => {
													const file = e.target.files[0];
													if (file && validateFile(file, "image")) {
														selectedFiles.image = file;
														handleImagePreview(file, (url) => {
															imagePreviewUrl = url;
														});
														editItem.image = file.name;
													}
												}}
											/>
										</div>
										<div class="row">
											<div class="col-12">
												<div class="mt-2">
													{#if imagePreviewUrl}
														<div class="mb-2">
															<p class="small text-muted mb-1">선택한 이미지 미리보기:</p>
															<div class="position-relative d-inline-block">
																<img
																	src={imagePreviewUrl}
																	alt="선택한 이미지"
																	class="img-thumbnail"
																	style="max-height: 150px;"
																/>
																<button
																	type="button"
																	class="btn btn-sm btn-danger position-absolute top-0 end-0 rounded-circle p-0 m-1"
																	style="width: 20px; height: 20px; line-height: 1; font-size: 14px;"
																	title="선택 취소"
																	on:click={handleCancelImage}
																>
																	<i class="bx bx-x"></i>
																</button>
															</div>
														</div>
													{:else if editItem.image && editItem.id}
														<p class="small text-muted mb-1">현재 이미지:</p>
														<div class="position-relative d-inline-block">
															<img
																src={getImageUrl(editItem.image, getResourceUrl)}
																alt="미리보기"
																class="img-thumbnail"
																style="max-height: 150px;"
																on:error={handleImageError}
															/>
														</div>
													{/if}
												</div>
											</div>
										</div>
									</div>
									<div class="row">
										<div class="col-md-6">
											<div class="mb-3">
												<label for="isActive" class="form-label">노출여부</label>
												<select
													class="form-select form-select-sm"
													id="isActive"
													value={editItem.isActive}
													on:change={(e) => handleInput("isActive", e.target.value === "true")}
												>
													{#each ACTIVE_OPTIONS as option}
														<option value={option.value}>{option.text}</option>
													{/each}
												</select>
											</div>
										</div>
									</div>
								</div>
								<div class="card-footer d-flex justify-content-between">
									<div>
										<small class="text-muted">생성: {Dates.defaultConvertFull(item.createdAt)}</small>
										<br />
										<small class="text-muted">수정: {Dates.defaultConvertFull(item.updatedAt)}</small>
									</div>
									<div>
										<button class="btn btn-primary btn-sm me-1" on:click={handleSave}>저장</button>
										<button class="btn btn-outline-secondary btn-sm" on:click={resetEdit}>취소</button>
									</div>
								</div>
							</div>
						</div>
					{:else}
						<!-- 일반 카드 -->
						<div style="width: 270px; margin-bottom: 1rem; padding: 0 0.5rem;">
							<div class="card h-100 border {getCategoryBorderColor(item.idCategory)} border-opacity-50">
								<div class="position-relative">
									{#if item.image}
										<div style="height: 150px; overflow: hidden;">
											<img
												src={item.image}
												alt={item.subjectKo}
												class="w-100 h-100 card-img-top"
												style="object-fit: cover; height: 150px;"
												on:error={handleImageError}
											/>
										</div>
									{:else}
										<div style="height: 150px; overflow: hidden;">
											<img src="/assets/img/nope-not-here.avif" alt="이미지 없음" style="width: 281px; height: 262px;" />
										</div>
									{/if}
									<!-- 배지를 이미지 위에 겹쳐서 표시 -->
									<div class="position-absolute top-0 start-0 p-2 d-flex justify-content-between w-100">
										<span class="badge {item.isActive ? 'bg-success text-dark' : 'bg-secondary text-dark'} fw-bold">
											{item.isActive ? "노출" : "미노출"}
										</span>
										<span class="badge {getHRLevelBadgeColor(item.group)}">
											{getHRLevelText(item.group)}
										</span>
									</div>
								</div>
								<div class="card-body p-2">
									<h6 class="card-title mb-1">
										{item.subjectKo}
									</h6>
									<p class="card-subtitle mb-2 text-muted small">
										{item.subjectEn}
									</p>
									<div class="mt-2">
										<p class="card-text mb-0 small">
											분류: {getCategoryName(item.idCategory)}
										</p>
										<p class="card-text mb-0 small">
											음원번호: {item.idMusic}
										</p>
									</div>
								</div>
								<div class="card-footer d-flex justify-content-between align-items-center p-2 bg-light">
									<small class="text-muted">ID: {item.id}</small>
									<div class="input-group justify-content-end">
										<!-- 재생 버튼 추가 -->
										{#if item.path}
											<button
												class="btn btn-sm btn-danger"
												on:click={() => handleListAudioPlay(item)}
												title={currentPlayingId === item.id && isPlaying ? "정지" : "재생"}
											>
												<i class="bx {currentPlayingId === item.id && isPlaying ? 'bx-stop' : 'bx-play'}"></i>
											</button>
										{/if}
										<button class="btn btn-sm btn-outline-primary" on:click={() => showDetail(item)}>상세</button>
										<button class="btn btn-sm btn-primary" on:click={() => fnProcessEdit(item)}>편집</button>
										<button class="btn btn-sm btn-outline-primary" on:click={() => fnDelete(item.id)}>삭제</button>
									</div>
								</div>
							</div>
						</div>
					{/if}
				{/each}
			</div>
		{/if}
	</div>
</div>

<!-- 모달 팝업 추가 -->
{#if showModal}
	<div
		class="modal fade {showModal ? 'show' : ''}"
		style="display: block;"
		tabindex="-1"
		aria-modal="true"
		role="dialog"
		transition:fade={{ duration: 200 }}
	>
		<div class="modal-dialog modal-dialog-centered modal-lg">
			<div class="modal-content">
				<div class="modal-header {!editItem.id ? 'bg-light' : ''}">
					<h5 class="modal-title d-flex align-items-center">
						{@html modalTitle}
					</h5>
					<button type="button" class="btn-close" on:click={closeModal}></button>
				</div>
				<div class="modal-body">
					<!-- 기본 정보 -->
					<div class="mb-4">
						<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
							<i class="bx bx-info-circle me-2"></i>
							기본 정보
						</h6>
						<div class="row">
							<div class="col-md-3">
								<div class="mb-3">
									<label for="idCategory" class="form-label">분류 인덱스</label>
									<select
										class="form-select form-select-sm"
										id="idCategory"
										value={editItem.idCategory}
										on:change={(e) => handleInput("idCategory", parseInt(e.target.value))}
									>
										{#each categories as category}
											<option value={category.id}>
												{category.subjectKo} ({category.id})
											</option>
										{/each}
									</select>
								</div>
							</div>
							<div class="col-md-3">
								<div class="mb-3">
									<label for="group" class="form-label">HR Level</label>
									<select
										class="form-select form-select-sm"
										id="group"
										value={editItem.group}
										on:change={(e) => handleInput("group", parseInt(e.target.value))}
									>
										{#each HR_LEVEL_OPTIONS as option}
											<option value={option.value}>{option.text}</option>
										{/each}
									</select>
								</div>
							</div>
							<div class="col-md-3">
								<div class="mb-3">
									<label for="idMusic" class="form-label">음원 고유번호</label>
									<input
										type="number"
										class="form-control form-control-sm"
										id="idMusic"
										value={editItem.idMusic}
										on:input={(e) => handleInput("idMusic", e.target.value)}
										placeholder="음원 고유번호를 입력하세요"
									/>
								</div>
							</div>
							<div class="col-md-3">
								<div class="mb-3">
									<label for="isActive" class="form-label">노출여부</label>
									<select
										class="form-select form-select-sm"
										id="isActive"
										value={editItem.isActive}
										on:change={(e) => handleInput("isActive", e.target.value === "true")}
									>
										{#each ACTIVE_OPTIONS as option}
											<option value={option.value}>{option.text}</option>
										{/each}
									</select>
								</div>
							</div>
						</div>
					</div>

					<!-- 다국어 제목 -->
					<div class="mb-4">
						<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
							<i class="bx bx-globe me-2"></i>
							다국어 제목
						</h6>
						<div class="row">
							<div class="col-md-4">
								<div class="mb-3">
									<label for="subjectKo" class="form-label">한글명</label>
									<input
										type="text"
										class="form-control form-control-sm"
										id="subjectKo"
										value={editItem.subjectKo}
										on:input={(e) => handleInput("subjectKo", e.target.value)}
										placeholder="한글 음원명을 입력하세요"
									/>
								</div>
							</div>
							<div class="col-md-4">
								<div class="mb-3">
									<label for="subjectEn" class="form-label">영문명</label>
									<input
										type="text"
										class="form-control form-control-sm"
										id="subjectEn"
										value={editItem.subjectEn}
										on:input={(e) => handleInput("subjectEn", e.target.value)}
										placeholder="영문 음원명을 입력하세요"
									/>
								</div>
							</div>
							<div class="col-md-4">
								<div class="mb-3">
									<label for="subjectJa" class="form-label">일본어명</label>
									<input
										type="text"
										class="form-control form-control-sm"
										id="subjectJa"
										value={editItem.subjectJa}
										on:input={(e) => handleInput("subjectJa", e.target.value)}
										placeholder="일본어 음원명을 입력하세요"
									/>
								</div>
							</div>
						</div>
						<div class="row">
							<div class="col-md-4">
								<div class="mb-3">
									<label for="subjectZhCn" class="form-label">중국어 간체</label>
									<input
										type="text"
										class="form-control form-control-sm"
										id="subjectZhCn"
										value={editItem.subjectZhCn}
										on:input={(e) => handleInput("subjectZhCn", e.target.value)}
										placeholder="중국어 간체 음원명을 입력하세요"
									/>
								</div>
							</div>
							<div class="col-md-4">
								<div class="mb-3">
									<label for="subjectZhTw" class="form-label">중국어 번체</label>
									<input
										type="text"
										class="form-control form-control-sm"
										id="subjectZhTw"
										value={editItem.subjectZhTw}
										on:input={(e) => handleInput("subjectZhTw", e.target.value)}
										placeholder="중국어 번체 음원명을 입력하세요"
									/>
								</div>
							</div>
						</div>
					</div>

					<!-- 리소스 정보 -->
					<div class="mb-4">
						<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
							<i class="bx bx-file me-2"></i>
							리소스 정보
						</h6>
						<div class="row">
							<div class="col-md-6">
								<div class="mb-3">
									<label for="image" class="form-label">이미지 경로</label>
									<div class="input-group input-group-sm">
										<input
											type="text"
											class="form-control form-control-sm"
											id="image"
											value={editItem.image}
											readOnly
											on:click={() => document.getElementById("imageFile").click()}
											placeholder="예: /v3/resources/sleep/17_Jazz/3005-47.jpg"
										/>
										<label class="input-group-text" for="imageFile">
											<i class="bx bx-upload"></i>
										</label>
										<input
											type="file"
											id="imageFile"
											accept="image/*"
											class="d-none"
											on:change={(e) => {
												const file = e.target.files[0];
												if (file && validateFile(file, "image")) {
													selectedFiles.image = file;
													handleImagePreview(file, (url) => {
														imagePreviewUrl = url;
													});
													editItem.image = file.name;
												}
											}}
										/>
									</div>
								</div>
							</div>
							<div class="col-md-6">
								<div class="mb-3">
									<label for="path" class="form-label">음원 경로</label>
									<div class="input-group input-group-sm">
										<input
											type="text"
											class="form-control form-control-sm"
											id="path"
											value={editItem.path}
											readOnly
											on:click={() => document.getElementById("audioFile").click()}
											placeholder="예: /v3/resources/sleep/17_Jazz/3005-47_557537051835022_01.aac"
										/>
										{#if editItem.path}
											<button
												type="button"
												class="btn btn-sm {isPlaying ? 'btn-danger' : 'btn-primary'}"
												title={isPlaying ? "중지" : "재생"}
												on:click={() =>
													isPlaying
														? stopAudio(
																audioPlayer,
																(v) => (audioPlayer = v),
																(v) => (isPlaying = v)
															)
														: playAudio(
																editItem.path,
																selectedFiles,
																(v) => (audioPlayer = v),
																(v) => (isPlaying = v),
																getResourceUrl
															)}
											>
												<i class="bx {isPlaying ? 'bx-stop' : 'bx-play'}"></i>
											</button>
										{/if}
										<label class="input-group-text" style="cursor: pointer;" for="audioFile">
											<i class="bx bx-upload"></i>
										</label>
										<input
											type="file"
											id="audioFile"
											accept="audio/*,.mp3,.wav,.aac,.m4a,.ogg,.flac"
											class="d-none"
											on:change={(e) => {
												const file = e.target.files[0];
												if (file && validateFile(file, "audio")) {
													selectedFiles.audio = file;
													editItem.path = file.name;
												}
											}}
										/>
									</div>
								</div>
							</div>
						</div>
						<div class="row">
							<div class="col-12">
								<div class="mt-2">
									{#if imagePreviewUrl}
										<div class="mb-2">
											<p class="small text-muted mb-1">선택한 이미지 미리보기:</p>
											<div class="position-relative d-inline-block">
												<img src={imagePreviewUrl} alt="선택한 이미지" class="img-thumbnail" style="max-height: 150px;" />
												<button
													type="button"
													class="btn btn-sm btn-danger position-absolute top-0 end-0 rounded-circle p-0 m-1"
													style="width: 20px; height: 20px; line-height: 1; font-size: 14px;"
													title="선택 취소"
													on:click={handleCancelImage}
												>
													<i class="bx bx-x"></i>
												</button>
											</div>
										</div>
									{:else if editItem.image && editItem.id}
										<p class="small text-muted mb-1">현재 이미지:</p>
										<div class="position-relative d-inline-block">
											<img
												src={getImageUrl(editItem.image, getResourceUrl)}
												alt="미리보기"
												class="img-thumbnail"
												style="max-height: 150px;"
												on:error={handleImageError}
											/>
										</div>
									{/if}
								</div>
							</div>
						</div>
					</div>
					{#if editItem.id}
						<div class="mb-4">
							<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
								<i class="bx bx-cog me-2"></i>
								시스템 정보
							</h6>
							<small class="text-muted">생성: {Dates.defaultConvertFull(editItem.createdAt)}</small>
							<br />
							<small class="text-muted">수정: {Dates.defaultConvertFull(editItem.updatedAt)}</small>
						</div>
						<!-- 노출여부 -->
						<div class="mb-4">
							<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
								<i class="bx bx-cog me-2"></i>
								노출여부
							</h6>
							<div class="form-check">
								<input
									class="form-check-input"
									type="checkbox"
									id="isActive"
									checked={editItem.isActive}
									on:change={(e) => handleInput("isActive", e.target.checked)}
								/>
								<label class="form-check-label" for="isActive">사용</label>
							</div>
						</div>
					{/if}
				</div>
				<div class="modal-footer d-flex justify-content-center">
					<button type="button" class="btn btn-outline-primary" on:click={closeModal} disabled={isSaving}>취소</button>
					<button type="button" class="btn btn-primary" on:click={handleSave} disabled={isSaving}>
						{#if isSaving}
							<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
							처리 중...
						{:else if editItem.id}
							수정
						{:else}
							신규 등록
						{/if}
					</button>
				</div>
			</div>
		</div>
	</div>
	<div class="modal-backdrop fade {showModal ? 'show' : ''}" transition:fade={{ duration: 200 }}></div>
{/if}

<!-- 상세보기 모달 팝업 -->
{#if showDetailModal && detailItem}
	<div
		class="modal fade {showDetailModal ? 'show' : ''}"
		style="display: block;"
		tabindex="-1"
		aria-modal="true"
		role="dialog"
		transition:fade={{ duration: 200 }}
	>
		<div class="modal-dialog modal-dialog-centered modal-xl">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title d-flex align-items-center">
						<i class="bx bx-music"></i>
						음원 상세 정보
					</h5>
					<button type="button" class="btn-close" on:click={closeDetailModal}></button>
				</div>
				<div class="modal-body">
					<div class="row">
						<!-- 좌측: 이미지 -->
						<div class="col-md-6">
							{#if detailItem.image}
								<div class="position-relative">
									<img
										src={detailItem.image}
										alt={detailItem.subjectKo}
										class="img-fluid rounded cursor-pointer"
										style="width: 100%; height: 512px; object-fit: contain;"
										on:error={handleImageError}
									/>
									{#if detailItem.image.includes("/v3/resources/")}
										<div class="position-absolute top-0 end-0 m-2">
											<button
												type="button"
												class="btn btn-sm btn-light rounded-circle"
												title="원본 이미지 보기"
												on:click={() => window.open(detailItem.image, "_blank")}
											>
												<i class="bx bx-fullscreen"></i>
											</button>
										</div>
									{/if}
								</div>
							{:else}
								<div style="height: 150px; overflow: hidden;">
									<img src="/assets/img/nope-not-here.avif" alt="이미지 없음" style="width: 281px; height: 262px;" />
								</div>
							{/if}
						</div>

						<!-- 우측: 메타정보 -->
						<div class="col-md-6">
							<!-- 기본 정보 -->
							<div class="mb-4">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-info-circle me-2"></i>
									기본 정보
								</h6>
								<div class="row mb-2">
									<div class="col-4 fw-bold">ID</div>
									<div class="col-8">
										{detailItem.idMusic}
									</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">분류 인덱스</div>
									<div class="col-8">
										{getCategoryName(detailItem.idCategory)}
									</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">HR Level</div>
									<div class="col-8">
										<span class="badge {getHRLevelBadgeColor(detailItem.group)}">
											{getHRLevelText(detailItem.group)}
										</span>
									</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">노출여부</div>
									<div class="col-8">
										<span class="badge {detailItem.isActive ? 'bg-success' : 'bg-secondary'}">
											{detailItem.isActive ? "노출" : "미노출"}
										</span>
									</div>
								</div>
							</div>

							<!-- 다국어 제목 -->
							<div class="mb-4">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-globe me-2"></i>
									다국어 제목
								</h6>
								<div class="row mb-2">
									<div class="col-4 fw-bold">한글명</div>
									<div class="col-8">
										{detailItem.subjectKo}
									</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">영문명</div>
									<div class="col-8">
										{detailItem.subjectEn}
									</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">일본어명</div>
									<div class="col-8">
										{detailItem.subjectJa}
									</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">중국어 간체</div>
									<div class="col-8">
										{detailItem.subjectZhCn}
									</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">중국어 번체</div>
									<div class="col-8">
										{detailItem.subjectZhTw}
									</div>
								</div>
							</div>

							<!-- 리소스 정보 -->
							<div class="mb-4">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-file me-2"></i>
									리소스 정보
								</h6>
								<div class="row mb-2">
									<div class="col-4 fw-bold">음원 경로</div>
									<div class="col-8 d-flex align-items-center">
										<span
											class="me-2 text-primary cursor-pointer text-truncate"
											on:click={() => downloadFile(detailItem.path, getResourceUrl)}
											title={getResourceUrl(detailItem.path)}
										>
											{getResourceUrl(detailItem.path)}
										</span>
										{#if detailItem.path}
											<button
												type="button"
												class="btn btn-sm {isPlaying ? 'btn-danger' : 'btn-primary'}"
												on:click={() =>
													isPlaying
														? stopAudio(
																audioPlayer,
																(v) => (audioPlayer = v),
																(v) => (isPlaying = v)
															)
														: playAudio(
																detailItem.path,
																selectedFiles,
																(v) => (audioPlayer = v),
																(v) => (isPlaying = v),
																getResourceUrl
															)}
											>
												<i class="bx {isPlaying ? 'bx-stop' : 'bx-play'}"></i>
											</button>
										{/if}
									</div>
								</div>
							</div>

							<!-- 시스템 정보 -->
							<div class="mb-4">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-cog me-2"></i>
									시스템 정보
								</h6>
								<div class="row mb-2">
									<div class="col-4 fw-bold">생성일시</div>
									<div class="col-8">
										{detailItem.createdAt ? Dates.defaultConvertFull(detailItem.createdAt) : "알 수 없음"}
									</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">수정일시</div>
									<div class="col-8">
										{detailItem.updatedAt ? Dates.defaultConvertFull(detailItem.updatedAt) : "알 수 없음"}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="modal-footer">
					<button type="button" class="btn btn-sm btn-primary" on:click={() => fnProcessEdit(detailItem)}>편집</button>
					<button type="button" class="btn btn-sm btn-outline-primary" on:click={closeDetailModal}>닫기</button>
				</div>
			</div>
		</div>
	</div>
	<div class="modal-backdrop fade {showDetailModal ? 'show' : ''}" transition:fade={{ duration: 200 }}></div>
{/if}

<style>
	.cursor-pointer {
		cursor: pointer;
	}

	.card-grid {
		display: flex;
		flex-wrap: wrap;
		gap: 1rem;
	}

	.card-item {
		flex: 0 0 270px;
		margin-bottom: 1rem;
	}

	@media (max-width: 576px) {
		.card-item {
			flex: 0 0 100%;
		}
	}

	.fade-container {
		opacity: 0;
		transition: opacity 0.15s linear;
	}

	.fade-container.show {
		opacity: 1;
	}

	.text-truncate {
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		max-width: 100%; /* 부모 요소의 너비에 맞게 조정 */
	}
</style>
